import { useState } from 'react';

// Utils
import api from 'frontend/js/api';

// Hooks
import useCustomerAccessToken from '@src/hooks/useCustomerAccessToken';
import useCustomerLogin from '@src/hooks/useCustomerLogin';


function useCustomerActivate() {
    // States
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // Hooks
    const { updateAccessToken } = useCustomerAccessToken();
    const { loginCustomer } = useCustomerLogin();

    const activateCustomer = async (
        password,
        confirmPassword,
        customerId,
        activationToken
    ) => {
        setLoading(true);
        setError('');
        setSuccess(false);

        const CUSTOMER_ACTIVATE_MUTATION = `
      mutation customerActivate($id: ID!, $input: CustomerActivateInput!) {
        customerActivate(id: $id, input: $input) {
          customer {
            id
            email
          }
          customerAccessToken {
            accessToken
            expiresAt
          }
          customerUserErrors {
            code
            field
            message
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

        // Create input fields
        const customerInput = {
            id: `gid://shopify/Customer/${customerId}`,
            input: {
                password,
                activationToken
            }
        };

        if (!password || !confirmPassword) {
            setError('Password and Password confirmation are required');
            setLoading(false);

            return;
        }

        if (password !== confirmPassword) {
            setError('Password and Password confirmation must match');
            setLoading(false);

            return;
        }

        try {
            const res = await fetch(api.getStorefrontGraphqlBaseUrl(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': api.getStorefrontToken()
                },
                body: JSON.stringify({
                    query: CUSTOMER_ACTIVATE_MUTATION,
                    variables: customerInput
                })
            });

            if (!res.ok) {
                throw new Error('Failed to activate password. Please try another time.');
            }

            const { data, errors } = await res.json();

            if (errors) throw new Error(errors[0].message);

            const { userErrors } = data.customerActivate;
            if (userErrors.length > 0) {
                let errorMessage = '';

                // Combine all error messages & Get fields with error
                userErrors.forEach((error) => {
                    errorMessage += `${error.message}\n`;
                });

                throw new Error(errorMessage);
            }

            const { customerUserErrors } = data.customerActivate;
            if (customerUserErrors.length > 0) {
                let errorMessage = '';

                // Combine all error messages & Get fields with error
                customerUserErrors.forEach((error) => {
                    errorMessage += `${error.message}\n`;
                });

                throw new Error(errorMessage);
            }

            // Set the new access token since the previous access token become invalid
            const newAccessToken = data.customerActivate.customerAccessToken.accessToken;
            updateAccessToken(newAccessToken);

            setSuccess(true);

            // Redirect to login page
            const email = data.customerActivate.customer.email;
            loginCustomer(email, password);
        } catch (error) {
            console.log(error);
            setError(error.message);

            return {
                success: false
            };
        } finally {
            setLoading(false);
        }
    };

    return { error, success, loading, activateCustomer };
}

export default useCustomerActivate;
